import { useEffect } from 'react';

/**
 * A hook to set the document title and meta description dynamically
 * 
 * @param title - The specific title (e.g. counter name) to display (if falsy, only app name is shown)
 * @param appName - The application name (defaults to "TapDonkey")
 * @param description - Optional description to set for meta description tag
 */
export function useDocumentTitle(
  title?: string, 
  appName: string = 'TapDonkey',
  description?: string
) {
  useEffect(() => {
    const defaultTitle = appName;
    const defaultDescription = 'TapDonkey - create and share tap counters';

    // Set document title
    document.title = title ? `${title} - ${appName}` : appName;
    
    // Update meta tags
    const pageTitle = title ? `${title} - ${appName}` : appName;
    const pageDescription = description || defaultDescription;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', pageDescription);
    }
    
    // Update Open Graph meta tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', pageTitle);
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', pageDescription);
    }
    
    // Update Twitter meta tags
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', pageTitle);
    }
    
    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', pageDescription);
    }
    
    // Restore original title and meta tags when component unmounts
    return () => {
      document.title = defaultTitle;
      
      // Reset meta description
      if (metaDescription) {
        metaDescription.setAttribute('content', defaultDescription);
      }
      
      // Reset Open Graph meta tags
      if (ogTitle) {
        ogTitle.setAttribute('content', defaultTitle);
      }
      
      if (ogDescription) {
        ogDescription.setAttribute('content', defaultDescription);
      }
      
      // Reset Twitter meta tags
      if (twitterTitle) {
        twitterTitle.setAttribute('content', defaultTitle);
      }
      
      if (twitterDescription) {
        twitterDescription.setAttribute('content', defaultDescription);
      }
    };
  }, [title, appName, description]);
} 